import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";
import { useAuth } from "@/context/AuthContext";
import { fmtEUR, fmtDate } from "@/lib/format";
import { planPrice, periodLabel } from "@/lib/pricing";
import { PaymentInstructions } from "@/components/PlanPicker";
import { HolderDetails } from "@/components/HolderDetails";
import { Vinti4CheckoutDialog } from "@/components/Vinti4Checkout";
import { CardLogos, fmtPlainCVE } from "@/components/PaymentLogos";
import { PeriodSelector, PlanPrice, PlanFeatures, EnterpriseCard, ContactLine } from "@/components/PricingParts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CircleNotch, CreditCard, Bank, Gear, DeviceMobile } from "@phosphor-icons/react";
import { toast } from "sonner";

function PlanCard({ plan, months, method, eurRate, busy, disabled, onChoose, current }) {
  const { base, final, isFounder } = planPrice(plan, months);
  return (
    <div className={`flex flex-col rounded-2xl border bg-card p-6 ${plan.highlight ? "border-primary shadow-lg shadow-primary/10" : "border-border"}`} data-testid={`plan-card-${plan.key}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-display text-xl font-extrabold">{plan.name}</h3>
        {plan.highlight && <Badge>Recomendado</Badge>}
      </div>
      <PlanPrice plan={plan} months={months} strike={false} />
      {isFounder && base > final && (
        <div className="mt-1 text-xs text-muted-foreground" data-testid={`plan-discount-${plan.key}`}>Preço Fundador · −{Math.round((1 - final / base) * 100)}% incluído (−{fmtPlainCVE(base - final)})</div>
      )}
      {method === "stripe" && final ? (
        <div className="mt-1 text-xs text-muted-foreground" data-testid={`plan-eur-${plan.key}`}>Cobrado em EUR: ≈ {fmtEUR(final / eurRate)} {months === 1 ? "por mês" : `a cada ${months} meses`}</div>
      ) : null}
      {method === "vinti4" && final ? (
        <div className="mt-1 text-xs font-semibold text-primary" data-testid={`plan-vinti4-${plan.key}`}>Valor a pagar: {fmtPlainCVE(final)}</div>
      ) : null}
      <PlanFeatures features={plan.features} />
      {current ? (
        <Button disabled variant="secondary" className="mt-6 rounded-full" data-testid={`current-${plan.key}`}>Plano atual</Button>
      ) : (
        <Button className="mt-6 rounded-full" variant={plan.highlight ? "default" : "outline"} disabled={disabled || !final}
          onClick={() => onChoose(plan.key)} data-testid={`choose-plan-${plan.key}`}>
          {busy === plan.key ? <CircleNotch className="animate-spin" /> : method === "stripe" ? <><CreditCard size={17} className="mr-1.5" /> Pagar com cartão</> : method === "vinti4" ? <><DeviceMobile size={17} className="mr-1.5" /> Pagar com Vinti4</> : "Escolher este plano"}
        </Button>
      )}
    </div>
  );
}

function MethodSelector({ method, setMethod, vinti4 }) {
  const opts = [
    ["manual", Bank, "Transferência bancária", "Pagamento em CVE, com referência única", true],
    ["vinti4", DeviceMobile, "Cartão Vinti4 / Visa / Mastercard / Amex", vinti4 ? "Pagamento seguro SISP, em CVE" : "Brevemente disponível", vinti4],
    ["stripe", CreditCard, "Cartão internacional (Stripe)", "Mesmo preço, cobrado em EUR, renovação automática", true],
  ];
  return (
    <div className="space-y-3">
    <p className="rounded-xl border border-primary/30 bg-secondary p-3 text-sm" data-testid="pay-method-guide">
      <b>Cartões emitidos em Cabo Verde (Vinti4 / Visa CV)</b> → pague com Vinti4. <b>Cartões estrangeiros</b> → pague com cartão internacional (Stripe).
      <span className="mt-1 block text-xs text-muted-foreground" data-testid="pay-price-parity">O preço é o mesmo em todos os métodos, sem taxas extra por pagar com cartão.</span>
    </p>
    <div className="grid gap-3 md:grid-cols-3">
      {opts.map(([k, Icon, title, sub, on]) => (
        <button key={k} onClick={() => on && setMethod(k)} disabled={!on} data-testid={`pay-method-${k}`}
          className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${method === k ? "border-primary bg-accent" : "border-border bg-card hover:bg-secondary"}`}>
          <Icon size={24} className="shrink-0 text-primary" />
          <div><div className="font-semibold">{title}</div><div className="text-xs text-muted-foreground">{sub}</div></div>
        </button>
      ))}
    </div>
    </div>
  );
}

function RenewBox({ plan, months, method, pending, onRenew, busy }) {
  const amount = planPrice(plan, months).final;
  const label = method === "vinti4" ? "Pagar com Cartão Vinti4" : pending ? "Ver instruções de transferência" : "Gerar referência de transferência";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-primary/50 bg-accent p-5" data-testid="renew-box">
      <div>
        <div className="font-display text-lg font-bold">Renovar o plano {plan.name} · {periodLabel(months)}</div>
        <div className="text-sm text-muted-foreground" data-testid="renew-amount">Valor: {fmtPlainCVE(amount)}</div>
      </div>
      <Button className="rounded-full" disabled={!!busy || !amount} onClick={onRenew} data-testid="renew-btn">
        {busy ? <CircleNotch className="animate-spin" /> : label}
      </Button>
    </div>
  );
}

export function PaymentOptions() {
  const { plans, durations, default_months: defMonths, founder, contact, eur_rate: eurRate, instructions, requests, entitlement, refresh, holder_complete: holderOk } = useSubscription();
  const { user } = useAuth();
  const [method, setMethod] = useState("manual");
  const [months, setMonths] = useState(defMonths || 12);
  const [busy, setBusy] = useState(null);
  const [v4, setV4] = useState(null);
  const [v4Target, setV4Target] = useState(null);
  useEffect(() => { if (defMonths) setMonths(defMonths); }, [defMonths]);
  useEffect(() => { api.get("/vinti4/config").then((r) => setV4(r.data)).catch(() => setV4({})); }, []);
  const [params] = useSearchParams();
  const renewPlan = params.get("renovar") ? (plans || []).find((p) => p.key === entitlement?.plan && !p.contact_only) : null;
  const renewMonths = (durations || []).includes(entitlement?.months) ? entitlement.months : defMonths;
  const renewInit = useRef(false);
  useEffect(() => {
    if (!renewPlan || !v4 || renewInit.current) return;
    renewInit.current = true;
    setMonths(renewMonths);
    setMethod(v4.available ? "vinti4" : "manual");
  }, [renewPlan, v4, renewMonths]);

  const canChoose = ["owner", "admin"].includes(user?.role);
  const isStripe = entitlement?.state === "active" && entitlement?.provider === "stripe";
  const pending = (requests || []).find((r) => r.status === "pendente");
  const selfServe = (plans || []).filter((p) => !p.contact_only);
  const empresa = (plans || []).find((p) => p.contact_only);

  const choose = async (plan) => {
    if (!holderOk) {
      toast.error("Complete os Dados do titular antes de pagar");
      document.getElementById("holder-details")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (method === "vinti4") {
      const p = selfServe.find((x) => x.key === plan);
      setV4Target({ plan: p, months, amount: planPrice(p, months).final });
      return;
    }
    setBusy(plan);
    try {
      if (method === "stripe") {
        const { data } = await api.post("/payments/checkout", { plan, months, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      await api.post("/plan/request", { plan, months });
      await refresh();
      toast.success("Pedido criado. Siga as instruções de pagamento.");
      setTimeout(() => document.getElementById("pay-instr")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(null);
  };
  const portal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) { toast.error(apiError(err)); setBusy(null); }
  };

  const renewPending = pending && renewPlan && pending.plan === renewPlan.key && pending.months === months;
  const renew = () => {
    if (method === "manual" && renewPending) {
      document.getElementById("pay-instr")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    choose(renewPlan.key);
  };

  return (
    <div className="space-y-6" data-testid="payment-options">
      <HolderDetails canEdit={canChoose} />
      {renewPlan && canChoose && method !== "stripe" && (
        <RenewBox plan={renewPlan} months={months} method={method} pending={renewPending} onRenew={renew} busy={busy} />
      )}
      <MethodSelector method={method} setMethod={setMethod} vinti4={!!v4?.available} />
      <div className="flex flex-wrap items-center justify-center gap-3">
        <PeriodSelector plans={selfServe} durations={durations || [1, 6, 12]} months={months} setMonths={setMonths} />
        {isStripe && canChoose && (
          <Button variant="outline" size="sm" className="rounded-full" disabled={!!busy} onClick={portal} data-testid="manage-subscription-btn">
            {busy === "portal" ? <CircleNotch className="animate-spin" /> : <><Gear size={16} className="mr-1.5" /> Gerir subscrição Stripe</>}
          </Button>
        )}
      </div>
      <div className={`grid gap-4 ${empresa ? "md:grid-cols-3" : "md:grid-cols-2"}`} data-testid="plan-picker">
        {selfServe.map((p) => (
          <PlanCard key={p.key} plan={p} months={months} method={method} eurRate={eurRate || 110.265} busy={busy}
            disabled={!canChoose || !!busy} onChoose={choose}
            current={isStripe && method === "stripe" && entitlement?.plan === p.key && entitlement?.months === months} />
        ))}
        {empresa ? <EnterpriseCard plan={empresa} contact={contact} className="rounded-2xl" /> : <ContactLine contact={contact} className="md:col-span-2" />}
      </div>
      {!canChoose && <p className="text-center text-sm text-muted-foreground">Apenas o proprietário ou um administrador pode escolher o plano.</p>}
      {method === "stripe" && <p className="text-center text-xs text-muted-foreground">Pagamento com cartão processado pela Stripe (em EUR, ao câmbio de {eurRate} CVE/EUR), com renovação automática. Pode cancelar a qualquer momento.</p>}
      {method === "vinti4" && <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground"><CardLogos className="h-5" />Pagamento seguro processado pela SISP (Vinti4), em CVE.</div>}
      <Vinti4CheckoutDialog key={v4Target ? `${v4Target.plan.key}-${v4Target.months}` : "none"} target={v4Target} config={v4} onClose={() => setV4Target(null)} />
      {method === "manual" && pending && (
        <div id="pay-instr" className="space-y-2">
          <h2 className="font-display text-lg font-bold">Pagamento pendente <span className="text-sm font-normal text-muted-foreground">· pedido de {fmtDate(pending.created_at)}</span></h2>
          <PaymentInstructions request={pending} instructions={instructions} discountPct={founder?.pct} />
        </div>
      )}
    </div>
  );
}
